import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Users, Send, Smartphone, Package, MessageSquare, ShieldOff, AlertTriangle, Info } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar } from 'recharts';

const SEGMENT_COLORS = { hot: 'hsl(0, 72%, 55%)', warm: 'hsl(33, 85%, 55%)', cold: 'hsl(206, 70%, 55%)', dead: 'hsl(215, 14%, 40%)' };
const SEGMENT_LABELS = { hot: 'Goracy', warm: 'Ciepli', cold: 'Zimni', dead: 'Martwi' };

const ChartTooltipStyle = { backgroundColor: 'hsl(222 16% 9%)', border: '1px solid hsl(222 12% 16%)', borderRadius: '8px', color: 'hsl(210 20% 96%)', fontSize: '12px' };

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [trends, setTrends] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/dashboard/stats'),
      api.get('/dashboard/trends', { params: { days: 7 } })
    ]).then(([s, t]) => { setStats(s.data); setTrends(t.data); })
    .catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="space-y-6" data-testid="dashboard-loading">
      <div><Skeleton className="h-8 w-48 mb-2" /><Skeleton className="h-4 w-72" /></div>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {[1,2,3,4].map(i => <Skeleton key={i} className="h-28 rounded-xl" />)}
      </div>
      <Skeleton className="h-64 rounded-xl" />
    </div>
  );

  const kpis = [
    { label: 'Kontakty', value: stats?.contacts || 0, icon: Users, color: 'text-primary' },
    { label: 'Kampanie', value: stats?.campaigns || 0, icon: Send, color: 'text-[hsl(var(--chart-2))]' },
    { label: 'Wyslane SMS', value: stats?.total_sent || 0, icon: MessageSquare, color: 'text-[hsl(var(--chart-3))]' },
    { label: 'Bramki online', value: `${stats?.gateways_online || 0}/${stats?.gateways_total || 0}`, icon: Smartphone, color: 'text-[hsl(var(--success))]' },
  ];

  const segmentData = stats?.segments ? Object.entries(stats.segments)
    .filter(([,v]) => v > 0)
    .map(([k,v]) => ({ name: SEGMENT_LABELS[k] || k, value: v, color: SEGMENT_COLORS[k] })) : [];

  const trendData = trends?.trends || [];
  const alerts = trends?.alerts || [];

  return (
    <div className="space-y-6" data-testid="dashboard">
      <div>
        <h1 className="font-heading text-2xl font-semibold tracking-tight">Dashboard</h1>
        <p className="text-sm text-muted-foreground mt-1">Przeglad systemu Torr.pl</p>
      </div>

      {/* Alerts */}
      {alerts.length > 0 && (
        <div className="space-y-2">
          {alerts.map((a, i) => (
            <div key={i} className={`flex items-center gap-3 px-4 py-2.5 rounded-lg border ${
              a.type === 'warning' ? 'bg-[hsl(var(--warning))]/5 border-[hsl(var(--warning))]/20 text-[hsl(var(--warning))]' :
              'bg-[hsl(var(--info))]/5 border-[hsl(var(--info))]/20 text-[hsl(var(--info))]'
            }`}>
              {a.type === 'warning' ? <AlertTriangle className="h-4 w-4 shrink-0" /> : <Info className="h-4 w-4 shrink-0" />}
              <span className="text-sm">{a.message}</span>
            </div>
          ))}
        </div>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {kpis.map((kpi, i) => (
          <Card key={i} className="border-border/50 bg-card/80">
            <CardContent className="pt-5 pb-4 px-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{kpi.label}</p>
                  <p className="text-2xl font-semibold font-heading tabular-nums mt-1" data-testid={`kpi-${kpi.label.toLowerCase().replace(/\s/g,'-')}`}>{kpi.value}</p>
                </div>
                <div className={`w-10 h-10 rounded-lg bg-muted/50 flex items-center justify-center ${kpi.color}`}>
                  <kpi.icon className="h-5 w-5" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Trend Chart + Segments */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 border-border/50 bg-card/80">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-base">Wysylki (ostatnie 7 dni)</CardTitle>
          </CardHeader>
          <CardContent>
            {trendData.length === 0 ? (
              <div className="py-8 text-center text-muted-foreground text-sm">Brak danych</div>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={trendData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(222 12% 16%)" />
                  <XAxis dataKey="label" tick={{ fill: 'hsl(215 14% 70%)', fontSize: 11 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: 'hsl(215 14% 70%)', fontSize: 11 }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={ChartTooltipStyle} />
                  <Bar dataKey="sent" name="Wyslane" fill="hsl(189, 70%, 45%)" radius={[4,4,0,0]} />
                  <Bar dataKey="failed" name="Bledy" fill="hsl(0, 72%, 52%)" radius={[4,4,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card className="border-border/50 bg-card/80">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-base">Segmenty kontaktow</CardTitle>
          </CardHeader>
          <CardContent>
            {segmentData.length === 0 ? (
              <div className="py-8 text-center text-muted-foreground text-sm">Brak kontaktow</div>
            ) : (
              <div className="flex flex-col items-center">
                <ResponsiveContainer width="100%" height={180}>
                  <PieChart>
                    <Pie data={segmentData} cx="50%" cy="50%" innerRadius={45} outerRadius={70} paddingAngle={3} dataKey="value">
                      {segmentData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                    </Pie>
                    <Tooltip contentStyle={ChartTooltipStyle} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex flex-wrap gap-3 mt-2">
                  {segmentData.map(s => (
                    <div key={s.name} className="flex items-center gap-1.5 text-xs">
                      <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: s.color }} />
                      {s.name}: {s.value}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent campaigns + Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-border/50 bg-card/80">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-base">Ostatnie kampanie</CardTitle>
          </CardHeader>
          <CardContent>
            {!stats?.recent_campaigns?.length ? (
              <div className="py-6 text-center text-muted-foreground text-sm">Brak kampanii. Utworz pierwsza!</div>
            ) : (
              <div className="space-y-2">
                {stats.recent_campaigns.map(c => (
                  <div key={c.id} className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-muted/50 transition-colors duration-150">
                    <div>
                      <p className="text-sm font-medium">{c.name}</p>
                      <p className="text-xs text-muted-foreground font-mono">{c.created_at?.slice(0, 10)}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-muted-foreground font-mono">{c.stats?.sent || 0} wysl.</span>
                      <Badge variant="outline" className={
                        c.status === 'completed' ? 'bg-[hsl(var(--success))]/10 text-[hsl(var(--success))] border-[hsl(var(--success))]/20' :
                        c.status === 'sending' ? 'bg-primary/10 text-primary border-primary/20' :
                        'bg-muted text-muted-foreground'}>{c.status}</Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-border/50 bg-card/80">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-base">Statystyki</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-muted/30">
                <div className="flex items-center gap-3"><Package className="h-4 w-4 text-muted-foreground" /><span className="text-sm">Produkty</span></div>
                <span className="text-sm font-semibold font-heading tabular-nums">{stats?.products || 0}</span>
              </div>
              <div className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-muted/30">
                <div className="flex items-center gap-3"><Send className="h-4 w-4 text-muted-foreground" /><span className="text-sm">Szablony</span></div>
                <span className="text-sm font-semibold font-heading tabular-nums">{stats?.templates || 0}</span>
              </div>
              <div className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-muted/30">
                <div className="flex items-center gap-3"><ShieldOff className="h-4 w-4 text-muted-foreground" /><span className="text-sm">Czarna lista</span></div>
                <span className="text-sm font-semibold font-heading tabular-nums">{stats?.blacklist || 0}</span>
              </div>
              <div className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-muted/30">
                <div className="flex items-center gap-3"><MessageSquare className="h-4 w-4 text-muted-foreground" /><span className="text-sm">SMS bledy</span></div>
                <span className="text-sm font-semibold font-heading tabular-nums text-[hsl(var(--destructive))]">{stats?.total_failed || 0}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
